import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Building2,
  Package,
  Plug,
  DollarSign,
  Zap,
  Rocket,
  Plus,
  Minus,
  HelpCircle,
  ArrowRight,
  X,
  MessageCircle,
  Mail,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEO from '../seo/SEO';
import { faqPageSchema } from '../seo/schemas';
import { toPlainText } from '../seo/extractText';

const CATEGORIES = [
  { id: 'about', label: 'About Shipmaster', icon: Building2 },
  { id: 'services', label: 'Services & Capabilities', icon: Package },
  { id: 'integrations', label: 'Integrations & Tech', icon: Plug },
  { id: 'pricing', label: 'Pricing & Contracts', icon: DollarSign },
  { id: 'operations', label: 'Operations', icon: Zap },
  { id: 'start', label: 'Getting Started', icon: Rocket },
];

const FAQS = [
  {
    cat: 'about',
    q: 'What types of brands do you work with?',
    a: (
      <>
        <p>
          We work with growing ecommerce brands selling through Shopify, TikTok Shop, Amazon
          (FBM and FBA), and wholesale channels. Our wheelhouse is{' '}
          <strong>beauty, skincare, wellness, apparel, lifestyle, and subscription</strong>{' '}
          products — categories where presentation, packaging, and customer experience matter as
          much as the unit cost.
        </p>
      </>
    ),
  },
  {
    cat: 'about',
    q: 'Where is Shipmaster located?',
    a: (
      <p>
        Our warehouse sits in <strong>Weston, FL</strong>, just outside Miami. South Florida is a
        sweet spot for shipping the East Coast, the Southeast, and most of the country in 1–3
        days on standard ground. Curious about transit times to your customers?{' '}
        <Link to="/zones" className="text-indigo-600 font-semibold hover:underline">
          Check our live zone map →
        </Link>
      </p>
    ),
  },
  {
    cat: 'about',
    q: 'What makes Shipmaster different from larger 3PLs?',
    a: (
      <>
        <p>
          Shipmaster was built by people who ran ecommerce brands first. That changes everything
          — from how we handle packaging to how fast we reply when something breaks at 6pm.
        </p>
        <ul>
          <li>You talk to real humans, not a ticket queue.</li>
          <li>We bend our process around your brand, not the other way around.</li>
          <li>Pricing is transparent and stays that way.</li>
          <li>We choose deep partnerships over a sprawling warehouse network.</li>
        </ul>
      </>
    ),
  },
  {
    cat: 'about',
    q: 'Are you a good fit for startup brands?',
    a: (
      <p>
        We support emerging brands, but we&apos;re a stronger fit for teams that already have{' '}
        <strong>consistent order volume</strong> or are{' '}
        <strong>actively scaling toward it</strong>. If presentation, retention, and customer
        experience are part of the founding playbook, we&apos;ll likely click. If you&apos;re still
        validating the product and shipping a handful of orders a month, we may not be the cheapest
        starting point — and that&apos;s okay.
      </p>
    ),
  },

  {
    cat: 'services',
    q: 'What fulfillment services do you offer?',
    a: (
      <ul>
        <li>DTC ecommerce fulfillment</li>
        <li>B2B / wholesale fulfillment with retailer routing & cartonization</li>
        <li>Amazon FBA prep + FBM fulfillment</li>
        <li>TikTok Shop fulfillment</li>
        <li>Returns processing with disposition reporting</li>
        <li>Kitting, bundling, and custom packaging</li>
        <li>Subscription box assembly</li>
        <li>Inventory storage & management</li>
      </ul>
    ),
  },
  {
    cat: 'services',
    q: 'Do you offer custom packaging?',
    a: (
      <>
        <p>
          Absolutely. The unboxing is part of the brand, and we treat it that way. We support:
        </p>
        <ul>
          <li>Branded boxes & mailers</li>
          <li>Custom inserts and thank-you cards</li>
          <li>Tissue paper, stickers, gift wrap</li>
          <li>Custom poly mailers</li>
          <li>Kitting & gift-with-purchase</li>
          <li>Influencer / PR mailers</li>
          <li>Subscription packaging programs</li>
        </ul>
      </>
    ),
  },
  {
    cat: 'services',
    q: 'Can you handle both DTC and wholesale orders?',
    a: (
      <p>
        Yes. We run DTC and B2B side-by-side — including retailer-specific routing requirements,
        pallet prep, GS1/UCC labeling, ASN generation, and cartonization rules. If you&apos;re
        scaling into Target, Whole Foods, Sephora, etc., bring us the routing guide and
        we&apos;ll work it.
      </p>
    ),
  },
  {
    cat: 'services',
    q: 'How do returns work?',
    a: (
      <p>
        Returns are received, inspected, and processed based on the rules you set:{' '}
        <strong>restock</strong>, <strong>quarantine</strong>, or <strong>dispose</strong>. We
        document any damage or non-sellable inventory and can sync dispositions back to your
        storefront so refunds and exchanges stay clean.
      </p>
    ),
  },
  {
    cat: 'services',
    q: 'Can you support Amazon FBM and FBA prep?',
    a: (
      <p>
        Yes — both. <strong>FBM</strong>: we ship Amazon orders directly to end customers under
        your seller account. <strong>FBA prep</strong>: we label, poly-bag, bundle, and pallet-prep
        per Amazon&apos;s current packaging guidelines, then ship inbound to the FC of your choice.
      </p>
    ),
  },
  {
    cat: 'services',
    q: 'Do you offer international shipping?',
    a: (
      <p>
        Yes, depending on product type, carrier availability, and destination country. We handle
        commercial invoices, harmonized codes, and customs documentation, and we&apos;ll route
        through the carrier that makes the most sense per shipment (UPS, FedEx, USPS, or DHL
        eCommerce).
      </p>
    ),
  },

  {
    cat: 'integrations',
    q: 'What ecommerce platforms do you integrate with?',
    a: (
      <>
        <p>Out of the box we integrate with:</p>
        <ul>
          <li>Shopify</li>
          <li>Amazon Seller Central (FBM)</li>
          <li>TikTok Shop</li>
          <li>WooCommerce</li>
          <li>Etsy</li>
          <li>Walmart Marketplace</li>
        </ul>
        <p>
          Selling on something else? Tell us what — we can usually wire it up via API, EDI, or
          spreadsheet drop, depending on the volume.
        </p>
      </>
    ),
  },
  {
    cat: 'integrations',
    q: 'Which carriers do you use?',
    a: (
      <>
        <p>We tender to the major carriers and rate-shop in real time:</p>
        <ul>
          <li>UPS</li>
          <li>FedEx</li>
          <li>USPS Ground Advantage / Priority</li>
          <li>DHL eCommerce (international)</li>
        </ul>
        <p>
          Carrier selection considers delivery speed, package size, destination zone, and cost.
          You can also lock to a specific carrier if your brand prefers it.
        </p>
      </>
    ),
  },
  {
    cat: 'integrations',
    q: 'Do you provide inventory visibility?',
    a: (
      <p>
        Yes. You get real-time inventory and order tracking through our fulfillment dashboard,
        plus exception alerts when something needs attention. Custom reports (cycle counts,
        velocity reports, returns dispositions) are available on request.
      </p>
    ),
  },

  {
    cat: 'pricing',
    q: 'How does pricing work?',
    a: (
      <>
        <p>Pricing is transparent and built around what you actually use:</p>
        <ul>
          <li>Monthly order volume</li>
          <li>Units per order</li>
          <li>Storage utilization (prorated)</li>
          <li>Packaging complexity</li>
          <li>B2B / retailer requirements</li>
          <li>Returns volume</li>
        </ul>
        <p>
          No hidden fulfillment fees, no fee schedules buried on page nine. Want a ballpark?{' '}
          <Link to="/zones#instant-estimate" className="text-indigo-600 font-semibold hover:underline">
            Try the instant estimate →
          </Link>
        </p>
      </>
    ),
  },
  {
    cat: 'pricing',
    q: 'Is there a monthly minimum?',
    a: (
      <p>
        We work best with brands doing consistent volume or actively scaling. Minimums depend on
        storage footprint, operational complexity, and the level of support required. We&apos;ll
        be upfront about whether you&apos;re a fit during the discovery call.
      </p>
    ),
  },
  {
    cat: 'pricing',
    q: 'Do you require long-term contracts?',
    a: (
      <p>
        No long-term contracts for most brands. We aim to earn the relationship every month
        through service quality — not by locking you in.
      </p>
    ),
  },
  {
    cat: 'pricing',
    q: 'Do you charge receiving fees?',
    a: (
      <p>
        Inbound receiving pricing depends on shipment type, pallet count, carton complexity, and
        inventory requirements (e.g., serial capture, lot tracking). All receiving costs are
        outlined clearly during onboarding — no surprises after the truck shows up.
      </p>
    ),
  },

  {
    cat: 'operations',
    q: 'How quickly do orders ship?',
    a: (
      <p>
        Most orders ship within <strong>24–48 business hours</strong>. Orders received before our
        daily cutoff (currently <strong>1:00 PM ET</strong>) typically ship the same business day,
        depending on volume and product requirements.
      </p>
    ),
  },
  {
    cat: 'operations',
    q: 'How long does onboarding take?',
    a: (
      <p>
        Most brands are live within <strong>a few business days</strong>. The biggest variable is
        inventory transit time — once your inventory hits our dock and your storefront is
        connected, we&apos;re usually shipping the same day.
      </p>
    ),
  },

  {
    cat: 'start',
    q: 'How do I get started?',
    a: (
      <>
        <p>Two ways:</p>
        <ol>
          <li>
            <Link to="/#contact" className="text-indigo-600 font-semibold hover:underline">
              Book a quick call
            </Link>{' '}
            — we&apos;ll talk current setup, volume, storage needs, and growth plans, then build
            a custom plan.
          </li>
          <li>
            Try the{' '}
            <Link to="/zones#instant-estimate" className="text-indigo-600 font-semibold hover:underline">
              instant estimate
            </Link>{' '}
            on our zone map page to see what your numbers look like in 30 seconds.
          </li>
        </ol>
      </>
    ),
  },
];

function highlightMatch(text, query) {
  if (!query) return text;
  const safe = String(text);
  const idx = safe.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {safe.slice(0, idx)}
      <mark className="bg-indigo-100 text-indigo-900 rounded px-0.5">
        {safe.slice(idx, idx + query.length)}
      </mark>
      {safe.slice(idx + query.length)}
    </>
  );
}

function FaqItem({ item, query, isOpen, onToggle }) {
  const cat = CATEGORIES.find((c) => c.id === item.cat);
  const Icon = cat?.icon || HelpCircle;
  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(0);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setMaxHeight(contentRef.current.scrollHeight);
    } else {
      setMaxHeight(0);
    }
  }, [isOpen, item]);

  return (
    <article
      className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
        isOpen
          ? 'bg-white border-indigo-200 shadow-md shadow-indigo-100/60'
          : 'bg-white border-slate-200 hover:border-indigo-200 hover:shadow-sm'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-4 text-left p-5 sm:p-6"
        aria-expanded={isOpen}
      >
        <span
          className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-colors ${
            isOpen ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-600'
          }`}
        >
          <Icon className="w-5 h-5" strokeWidth={2} />
        </span>
        <div className="flex-1 pt-1">
          <p className="text-xs font-bold uppercase tracking-widest text-indigo-500 mb-1">
            {cat?.label}
          </p>
          <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug">
            {highlightMatch(item.q, query)}
          </h3>
        </div>
        <span
          className={`flex-shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-200 ${
            isOpen
              ? 'bg-indigo-600 border-indigo-600 text-white rotate-180'
              : 'bg-white border-slate-200 text-slate-500 group-hover:border-indigo-300'
          }`}
        >
          {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </span>
      </button>
      <div
        style={{ maxHeight: `${maxHeight}px` }}
        className="transition-[max-height] duration-300 ease-in-out overflow-hidden"
      >
        <div ref={contentRef} className="px-5 sm:px-6 pb-6 pl-[calc(2.75rem+1rem+1.5rem)] sm:pl-[calc(2.75rem+1rem+1.5rem)]">
          <div className="text-slate-700 leading-relaxed text-[15px] faq-prose">{item.a}</div>
        </div>
      </div>
    </article>
  );
}

export default function FAQ() {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [openIds, setOpenIds] = useState(new Set());

  const filtered = useMemo(() => {
    let items = FAQS;
    if (activeCategory !== 'all') {
      items = items.filter((f) => f.cat === activeCategory);
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      items = items.filter((f) => {
        const inQ = f.q.toLowerCase().includes(q);
        // Render answer to a string approximation for searching by walking simple cases
        const inA = String(JSON.stringify(f.a) || '').toLowerCase().includes(q);
        return inQ || inA;
      });
    }
    return items;
  }, [activeCategory, query]);

  const totalCounts = useMemo(() => {
    const counts = { all: FAQS.length };
    CATEGORIES.forEach((c) => {
      counts[c.id] = FAQS.filter((f) => f.cat === c.id).length;
    });
    return counts;
  }, []);

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenIds(new Set(filtered.map((_, i) => i)));
  const collapseAll = () => setOpenIds(new Set());

  // Build FAQPage schema once from the same FAQ source-of-truth used for the UI.
  // Keeps copy in one place and feeds Google's FAQ rich-result eligibility.
  const faqSchema = useMemo(
    () => faqPageSchema(FAQS.map((f) => ({ question: f.q, answer: toPlainText(f.a) }))),
    [],
  );

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title="3PL FAQ — Pricing, Onboarding, Shipping & Returns"
        description="Answers to the questions ecommerce founders ask before switching 3PLs: pricing, onboarding timelines, integrations, returns, custom packaging, and more."
        keywords="3PL FAQ, 3PL pricing questions, 3PL onboarding, fulfillment FAQ, Shopify 3PL questions, ecommerce returns process"
        schemas={[faqSchema]}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-16 sm:pt-20 pb-10 overflow-hidden">
        <div className="absolute inset-0 -z-10 brand-soft-bg" />
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-indigo-200/30 blur-3xl" />
          <div className="absolute top-32 -left-32 w-[360px] h-[360px] rounded-full bg-blue-200/25 blur-3xl" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-indigo-100 text-indigo-700 text-xs font-bold uppercase tracking-widest shadow-sm">
            <HelpCircle className="w-3.5 h-3.5" /> Help Center
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Questions?{' '}
            <span className="brand-gradient-text">We&apos;ve got answers.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto">
            Search, filter, or scan by category. Still stuck? A real person is one click away.
          </p>

          {/* Search bar */}
          <div className="mt-9 max-w-2xl mx-auto relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search e.g. 'pricing', 'TikTok Shop', 'returns'..."
              className="h-14 pl-14 pr-12 text-base rounded-2xl bg-white border-slate-200 shadow-sm focus-visible:ring-2 focus-visible:ring-indigo-500"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Category pills */}
      <section className="pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <CategoryPill
              active={activeCategory === 'all'}
              onClick={() => setActiveCategory('all')}
              count={totalCounts.all}
            >
              All
            </CategoryPill>
            {CATEGORIES.map((c) => (
              <CategoryPill
                key={c.id}
                active={activeCategory === c.id}
                onClick={() => setActiveCategory(c.id)}
                icon={c.icon}
                count={totalCounts[c.id]}
              >
                {c.label}
              </CategoryPill>
            ))}
          </div>
        </div>
      </section>

      {/* Result count + expand/collapse */}
      <section>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between text-sm">
            <p className="text-slate-500">
              {filtered.length} {filtered.length === 1 ? 'result' : 'results'}
              {query && (
                <>
                  {' '}for <span className="font-semibold text-slate-900">&ldquo;{query}&rdquo;</span>
                </>
              )}
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={expandAll}
                className="text-indigo-600 hover:text-indigo-700 font-semibold transition-colors"
              >
                Expand all
              </button>
              <span className="text-slate-300">·</span>
              <button
                type="button"
                onClick={collapseAll}
                className="text-slate-500 hover:text-indigo-600 font-medium transition-colors"
              >
                Collapse all
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ list */}
      <section className="py-8 sm:py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 ? (
            <NoResults onClear={() => { setQuery(''); setActiveCategory('all'); }} />
          ) : (
            <div className="space-y-3">
              {filtered.map((item, idx) => {
                const id = `${item.cat}-${item.q}`;
                return (
                  <FaqItem
                    key={id}
                    item={item}
                    query={query}
                    isOpen={openIds.has(id)}
                    onToggle={() => toggle(id)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Still have questions CTA */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-indigo-50 border border-indigo-100 rounded-3xl p-10 sm:p-14 text-center">
            <div
              className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-indigo-200/40 blur-3xl pointer-events-none"
              aria-hidden="true"
            />
            <div className="relative">
              <div className="w-14 h-14 mx-auto rounded-2xl brand-gradient flex items-center justify-center mb-5 shadow-lg shadow-indigo-500/20">
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
                Still have questions?
              </h2>
              <p className="mt-3 text-lg text-slate-600 max-w-xl mx-auto">
                Talk to a real person. No bots, no ticket queues — just an operator who actually
                runs the warehouse.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  asChild
                  size="lg"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-12 px-7 text-base font-semibold shadow-md hover:shadow-lg transition-all duration-200 group"
                >
                  <Link to="/#contact" className="inline-flex items-center gap-2">
                    Book a Call
                    <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="rounded-full h-12 px-7 border-slate-300 text-slate-800 hover:border-indigo-500 hover:text-indigo-600 transition-colors"
                >
                  <a href="mailto:info@shipmasterprep.com" className="inline-flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email info@shipmasterprep.com
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function CategoryPill({ active, onClick, icon: Icon, count, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-150 ${
        active
          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
          : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:text-indigo-600'
      }`}
    >
      {Icon && <Icon className="w-4 h-4" />}
      <span>{children}</span>
      <span
        className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
          active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function NoResults({ onClear }) {
  return (
    <div className="text-center py-16 px-6 bg-slate-50/60 border border-dashed border-slate-200 rounded-2xl">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-white border border-slate-200 flex items-center justify-center mb-4">
        <Search className="w-6 h-6 text-slate-400" />
      </div>
      <h3 className="text-lg font-bold text-slate-900">No results found</h3>
      <p className="mt-2 text-slate-600">
        Try a different search term or clear filters to see all questions.
      </p>
      <Button onClick={onClear} variant="outline" className="mt-5 rounded-full">
        Clear filters
      </Button>
    </div>
  );
}
